

import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;
const token = localStorage.getItem('accessToken');

export default function BusinessSection({ caseId, sofaResponse, caseData }) {
  const [businesses, setBusinesses] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newBiz, setNewBiz] = useState({
    business_type: 'sole_proprietor',
    business_name: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip_code: '',
    nature_of_business: '',
    accountant_name: '',
    ein: '',
    start_date: '',
    end_date: ''
  });
  const [editBiz, setEditBiz] = useState(null);

  // Fetch existing entries
  useEffect(() => {
    if (!sofaResponse) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/cases/${caseId}/sofa-businesses/?sofa_response=${sofaResponse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to fetch businesses');
        setBusinesses(await res.json());
      } catch (err) {
        console.error(err);
      }
    })();
  }, [caseId, sofaResponse]);

  // Open Add
  function openAddModal() {
    setNewBiz({
      business_type: 'sole_proprietor',
      business_name: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip_code: '',
      nature_of_business: '',
      accountant_name: '',
      ein: '',
      start_date: '',
      end_date: ''
    });
    setModalOpen(true);
  }

  // Open Edit
  function openEditModal(item) {
    setEditBiz({ ...item });
    setEditModalOpen(true);
  }

  // Save new
  async function handleSaveNew() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-businesses/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(newBiz)
        }
      );
      if (!res.ok) throw new Error('Failed to add business');
      const created = await res.json();
      setBusinesses(prev => [...prev, created]);
      setModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  }

  // Save edit
  async function handleSaveEdit() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-businesses/${editBiz.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editBiz)
        }
      );
      if (!res.ok) throw new Error('Failed to update business');
      const updated = await res.json();
      setBusinesses(prev =>
        prev.map(b => (b.id === updated.id ? updated : b))
      );
      setEditModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  }

  // Delete
  async function handleDelete(id) {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-businesses/${id}/`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!res.ok) throw new Error('Failed to delete business');
      setBusinesses(prev => prev.filter(b => b.id !== id));
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <section className="p-6 bg-white shadow-lg rounded mb-6">
      <h2 className="text-xl font-semibold mb-4">Question 27: Business Interests</h2>
      <p className="text-sm text-gray-600 mb-4">
            List connections or ownerships in a business. Include sole-proprietor, llc, partner, corporate interests.
        </p>
      {businesses.length === 0 ? (
        <p>No business entries found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {businesses.map(item => (
            <div
              key={item.id}
              className="relative p-4 bg-white shadow rounded cursor-pointer hover:shadow-md transition"
              onClick={() => openEditModal(item)}
            >
              <button
                onClick={e => { e.stopPropagation(); handleDelete(item.id); }}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" clipRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000
                       2h14a1 1 0 000-2h-2V3a1 1 0 00-1-1H6zM4
                       6v10a2 2 0 002 2h8a2 2 0 002-2V6H4zm3
                       2a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 00-2
                       0zm4 0a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0
                       00-2 0z"/>
                </svg>
              </button>
              <h3 className="font-medium">{item.business_name}</h3>
              <p className="mt-1 text-sm"><strong>Type:</strong> {item.business_type}</p>
              <p className="mt-1 text-sm">
                {item.street1}{item.street2 ? `, ${item.street2}` : ''}, {item.city}, {item.state} {item.zip_code}
              </p>
              <p className="mt-1 text-sm"><strong>Nature:</strong> {item.nature_of_business}</p>
              {item.accountant_name && <p className="mt-1 text-sm"><strong>Accountant:</strong> {item.accountant_name}</p>}
              {item.ein && <p className="mt-1 text-sm"><strong>EIN:</strong> {item.ein}</p>}
              <p className="mt-1 text-sm"><strong>Period:</strong> {item.start_date || '—'} to {item.end_date || '—'}</p>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={openAddModal}
        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        Add Business
      </button>

      {/* Add Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-lg overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold mb-4">Add Business</h3>
            <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Business Type</label>
                <select
                    className="w-full border rounded px-2 py-1"
                    value={newBiz.business_type}
                    onChange={e => setNewBiz(prev => ({ ...prev, business_type: e.target.value }))}
                >
                  <option value="sole_proprietor">Sole Proprietor</option>
                  <option value="llc">LLC</option>
                  <option value="partnership">Partnership</option>
                  <option value="officer">Officer</option>
                  <option value="shareholder">Corporation</option>
                </select>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Business Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newBiz.business_name}
                onChange={e => setNewBiz(prev => ({ ...prev, business_name: e.target.value }))}
              />
            </div>
            <div className="flex space-x-2 mb-3">
              {caseData.debtors?.find(d => d.is_filing) && (
                <button
                  type="button"
                  onClick={() => {
                    const d = caseData.debtors.find(d => d.is_filing);
                    setNewBiz(prev => ({
                      ...prev,
                      street1: d.street || '',
                      street2: d.street2 || '',
                      city: d.city || '',
                      state: d.state || '',
                      zip_code: d.zip_code || ''
                    }));
                  }}
                  className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                >
                  Use Debtor Address
                </button>
              )}
              {caseData.debtors?.find(d => (d.is_filing && !d.is_primary)) && (
                <button
                  type="button"
                  onClick={() => {
                    const d = caseData.debtors.find(d => (!d.is_primary && d.is_filing));
                    setNewBiz(prev => ({
                      ...prev,
                      street1: d.street || '',
                      street2: d.street2 || '',
                      city: d.city || '',
                      state: d.state || '',
                      zip_code: d.zip_code || ''
                    }));
                  }}
                  className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                >
                  Use Spouse Address
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <input
                type="text"
                placeholder="Street1"
                className="w-full border rounded px-2 py-1"
                value={newBiz.street1}
                onChange={e => setNewBiz(prev => ({ ...prev, street1: e.target.value }))}
              />
              <input
                type="text"
                placeholder="Street2"
                className="w-full border rounded px-2 py-1"
                value={newBiz.street2}
                onChange={e => setNewBiz(prev => ({ ...prev, street2: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <input
                  type="text"
                  placeholder="City"
                  className="w-full border rounded px-2 py-1"
                  value={newBiz.city}
                  onChange={e => setNewBiz(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <input
                  type="text"
                  placeholder="State"
                  className="w-full border rounded px-2 py-1"
                  value={newBiz.state}
                  onChange={e => setNewBiz(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <input
                  type="text"
                  placeholder="Zip"
                  className="w-full border rounded px-2 py-1"
                  value={newBiz.zip_code}
                  onChange={e => setNewBiz(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Nature of Business</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newBiz.nature_of_business}
                onChange={e => setNewBiz(prev => ({ ...prev, nature_of_business: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Accountant Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newBiz.accountant_name}
                onChange={e => setNewBiz(prev => ({ ...prev, accountant_name: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">EIN</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newBiz.ein}
                onChange={e => setNewBiz(prev => ({ ...prev, ein: e.target.value }))}
              />
            </div>
            <div className="flex space-x-2 mb-3">
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Start Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={newBiz.start_date}
                  onChange={e => setNewBiz(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">End Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={newBiz.end_date}
                  onChange={e => setNewBiz(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setModalOpen(false)}
                className="px-3 py-1 rounded border border-gray-400 text-gray-700 bg-white hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNew}
                className="px-4 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editBiz && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-lg overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold mb-4">Edit Business</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Business Type</label>
                <select
                    className="w-full border rounded px-2 py-1"
                    value={newBiz.business_type}
                    onChange={e => setNewBiz(prev => ({ ...prev, business_type: e.target.value }))}
                >
                  <option value="sole_proprietor">Sole Proprietor</option>
                  <option value="llc">LLC</option>
                  <option value="partnership">Partnership</option>
                  <option value="officer">Officer</option>
                  <option value="shareholder">Corporation</option>
                </select>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Business Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editBiz.business_name}
                onChange={e => setEditBiz(prev => ({ ...prev, business_name: e.target.value }))}
              />
            </div>
            <div className="flex space-x-2 mb-3">
            {caseData.debtors?.find(d => d.is_filing) && (
                <button
                  type="button"
                  onClick={() => {
                    const d = caseData.debtors.find(d => d.is_primary);
                    setEditBiz(prev => ({
                      ...prev,
                      street1: d.street || '',
                      street2: d.street2 || '',
                      city: d.city || '',
                      state: d.state || '',
                      zip_code: d.zip_code || ''
                    }));
                  }}
                  className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                >
                  Use Debtor Address
                </button>
              )}
              {caseData.debtors?.find(d => (!d.is_primary && d.is_filing)) && (
                <button
                  type="button"
                  onClick={() => {
                    const d = caseData.debtors.find(d => (!d.is_primary && d.is_filing));
                    setEditBiz(prev => ({
                      ...prev,
                      street1: d.street || '',
                      street2: d.street2 || '',
                      city: d.city || '',
                      state: d.state || '',
                      zip_code: d.zip_code || ''
                    }));
                  }}
                  className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                >
                  Use Spouse Address
                </button>
              )}
              </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <input
                type="text"
                placeholder="Street1"
                className="w-full border rounded px-2 py-1"
                value={editBiz.street1}
                onChange={e => setEditBiz(prev => ({ ...prev, street1: e.target.value }))}
              />
              <input
                type="text"
                placeholder="Street2"
                className="w-full border rounded px-2 py-1"
                value={editBiz.street2}
                onChange={e => setEditBiz(prev => ({ ...prev, street2: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <input
                  type="text"
                  placeholder="City"
                  className="w-full border rounded px-2 py-1"
                  value={editBiz.city}
                  onChange={e => setEditBiz(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <input
                  type="text"
                  placeholder="State"
                  className="w-full border rounded px-2 py-1"
                  value={editBiz.state}
                  onChange={e => setEditBiz(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <input
                  type="text"
                  placeholder="Zip"
                  className="w-full border rounded px-2 py-1"
                  value={editBiz.zip_code}
                  onChange={e => setEditBiz(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Nature of Business</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editBiz.nature_of_business}
                onChange={e => setEditBiz(prev => ({ ...prev, nature_of_business: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Accountant Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editBiz.accountant_name}
                onChange={e => setEditBiz(prev => ({ ...prev, accountant_name: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">EIN</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editBiz.ein}
                onChange={e => setEditBiz(prev => ({ ...prev, ein: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Start Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={editBiz.start_date}
                  onChange={e => setEditBiz(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">End Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={editBiz.end_date}
                  onChange={e => setEditBiz(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setEditModalOpen(false)}
                className="px-3 py-1 rounded border border-gray-400 text-gray-700 bg-white hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}